import {
    Component,
    Input,
    OnInit,
    OnChanges,
    ViewEncapsulation,
    DestroyRef, inject
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import {BehaviorSubject, combineLatestWith, Observable, Subscription} from 'rxjs';
import { map } from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { SharedFacadeService } from '@emm/shared/services/shared-facade.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ItemLocale, Locale, LocaleMappings} from '@emm/core/models/locale.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ItemsFacadeService} from '@emm/management/services/items-facade.service';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-language-form',
    styleUrls: ['./language-form.component.scss'],
    templateUrl: './language-form.component.html',
    standalone: false
})
export class LanguageFormComponent implements OnInit, OnChanges {

    @Input() attributeSelected: ItemLocale;

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    disabled$ = this.authFacade.isReadOnly$.pipe(
        combineLatestWith(this.itemsFacade.anyLoading$),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );
    fields: FormlyFieldConfig[];
    form: FormGroup;
    inputType: string;
    isReadOnly$ = this.authFacade.isReadOnly$;
    item: {
        disabled: boolean,
        itemClass: string,
        itemType: string,
        itemTypeName: string,
        lastUpdatedBy: string,
        lastUpdatedDate: string,
        locked: boolean,
        name: string,
        tag: string
    };
    itemDetail$ = this.itemsFacade.itemDetail$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(item => this.item = item);
    locale: Locale;
    localeMappings: LocaleMappings;
    localeMappings$: Subscription;
    localeSelected$ = this.sharedFacade.localeSelected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(locale => this.locale = locale);
    model = {};
    options: FormlyFormOptions = {};
    pipe = new DatePipe('en-US');
    readOnly: boolean;
    readOnlyFromAuth: boolean;
    localesLoaded = new BehaviorSubject<boolean>(false);
    render$ = new BehaviorSubject<boolean>(null);

    constructor(
        private authFacade: AuthFacadeService,
        private itemsFacade: ItemsFacadeService,
        private sharedFacade: SharedFacadeService,
    ) { }



    ngOnInit(): void {
        this.render$.next(false)
    }

    ngOnChanges(): void {
        this.localeMappings$ = this.sharedFacade.localeMappings$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(mappings => {
            this.localeMappings = mappings;
            const group: any = {};
            for (const formField of this.attributeSelected.values) {
                group[`${formField.language}-${formField.country}`] =
                    this.attributeSelected.metadata.required ? new FormControl({ value: formField.value,
                            disabled: this.attributeSelected.metadata.readOnly}, Validators.required) :
                        new FormControl({ value: formField.value,
                            disabled: this.attributeSelected.metadata.readOnly});
            }
            this.form = new FormGroup(group);
            this.localesLoaded.next(true);
            this.render$.next(true);
        });
        this.isReadOnly$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(readOnly => this.readOnlyFromAuth = readOnly);
        if (this.attributeSelected) {

            this.inputType = this.attributeSelected.metadata.type;
            this.readOnly = this.attributeSelected.metadata.readOnly;

            this.model = this.attributeSelected.values.reduce((result, { country, language, value }) => ({
                ...result,
                [`${language}-${country}`]: typeof value === 'boolean' ? value.toString() : value
            }), {});
        }
    }

    cancel(): void {
        this.options.resetModel();
    }

    hasChanges(): boolean {
        return this.form && this.form.dirty;
    }
    isFormReady(): boolean {
        return this.form && this.form.dirty && !this.form.invalid;
    }
}
